import { useCallback, useEffect, useMemo, useState } from "react";

import { buildPoseSequence, type PoseFrame } from "@nav-games/semaphore";

import { BroadcastToggle } from "../../ui/BroadcastToggle";
import { HelpButton, HelpPanel } from "../../ui/HelpPanel";
import { SemaphoreFigure } from "../../ui/SemaphoreFigure";

interface TranslateProps {
  onNavigate: (to: string) => void;
}

const POSE_MS = 1200;
const PREFIX_MS = 700;
const SPACE_MS = 600;

/// How long to show each pose. Letters / digits get a full second
/// of dwell time; the "numbers follow" prefix flashes briefly; the
/// rest pose serves as a word- or number-group break.
function durationFor(frame: PoseFrame): number {
  switch (frame.kind) {
    case "letter":
    case "digit":
      return POSE_MS;
    case "numericPrefix":
      return PREFIX_MS;
    case "rest":
      return SPACE_MS;
  }
}

function captionFor(frame: PoseFrame): string {
  switch (frame.kind) {
    case "numericPrefix":
      return "Numbers follow…";
    case "digit":
      return "(number)";
    default:
      return "";
  }
}

export function Translate({ onNavigate }: TranslateProps) {
  const [text, setText] = useState("");
  const [hidden, setHidden] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const frames = useMemo(() => buildPoseSequence(text), [text]);

  const [playing, setPlaying] = useState(false);
  const [cursor, setCursor] = useState(0);

  // Drive the playback timer when `playing` is true. On stop, the
  // cursor resets so the next play starts from the top.
  useEffect(() => {
    if (!playing || frames.length === 0) return;
    const frame = frames[cursor];
    if (!frame) {
      setPlaying(false);
      setCursor(0);
      return;
    }
    const t = setTimeout(() => {
      if (cursor + 1 >= frames.length) {
        setPlaying(false);
        setCursor(0);
      } else {
        setCursor((c) => c + 1);
      }
    }, durationFor(frame));
    return () => clearTimeout(t);
  }, [playing, cursor, frames]);

  const play = useCallback(() => {
    if (frames.length === 0) return;
    setCursor(0);
    setPlaying(true);
  }, [frames]);

  const stop = useCallback(() => {
    setPlaying(false);
    setCursor(0);
  }, []);

  const currentFrame = playing ? frames[cursor] ?? null : null;
  const currentPose =
    currentFrame && currentFrame.kind !== "rest" ? currentFrame.pose : null;
  const currentCaption = currentFrame ? captionFor(currentFrame) : "";

  // Count only "content" frames (letters + digits) for the
  // header — prefix and rest frames don't carry a character.
  const isContent = (f: PoseFrame) => f.kind === "letter" || f.kind === "digit";
  const contentFrameCount = frames.filter(isContent).length;
  const contentCursor = playing
    ? frames.slice(0, cursor + 1).filter(isContent).length
    : 0;

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
        <h2 style={{ margin: 0 }}>Translate</h2>
        <HelpButton onOpen={() => setHelpOpen(true)} />
      </div>
      <HelpPanel section={helpOpen ? "semaphore" : null} onClose={() => setHelpOpen(false)} />
      <p className="muted">
        Type a message — semaphore poses play in sequence. Digits use
        the proper numeric-signal mechanic: a "numbers follow" prefix
        pose, then the digit-as-letter pose for each number. Hit
        "Hide message" to cover the typed text and show only the
        signaller — hand the phone over for someone else to decode.
      </p>

      {!hidden && (
        <div className="card">
          <label className="small muted" htmlFor="msg">
            Text (letters, digits, spaces)
          </label>
          <textarea
            id="msg"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="ANCHOR 12"
            rows={2}
            style={{
              width: "100%",
              marginTop: 4,
              padding: 8,
              fontFamily: "inherit",
              fontSize: 16,
              boxSizing: "border-box",
            }}
          />
          <p className="small muted">
            {contentFrameCount} character{contentFrameCount === 1 ? "" : "s"} · approx{" "}
            {Math.round(
              frames.reduce((acc, f) => acc + durationFor(f), 0) / 1000,
            )}{" "}
            s playback
          </p>
          <button
            className="secondary"
            onClick={() => setHidden(true)}
            disabled={text.length === 0}
          >
            🙈 Hide message
          </button>
        </div>
      )}

      <div className="card" style={{ textAlign: "center" }}>
        {hidden && (
          <button
            className="secondary"
            onClick={() => setHidden(false)}
            style={{ marginBottom: 12 }}
          >
            👁 Reveal message
          </button>
        )}
        <SemaphoreFigure pose={currentPose} size={200} />
        <p
          className="small muted"
          style={{ minHeight: "1.2em", marginTop: 4 }}
        >
          {currentCaption !== ""
            ? currentCaption
            : playing && currentFrame && isContent(currentFrame)
              ? `Showing ${contentCursor} of ${contentFrameCount}`
              : playing
                ? "(rest)"
                : "Tap play to start"}
        </p>
        <div
          style={{
            display: "flex",
            gap: 8,
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          {!playing ? (
            <button onClick={play} disabled={frames.length === 0}>
              ▶ Play
            </button>
          ) : (
            <button className="secondary" onClick={stop}>
              ⏹ Stop
            </button>
          )}
        </div>
      </div>

      <BroadcastToggle module="semaphore" text={text} />

      <button className="secondary" onClick={() => onNavigate("/play/semaphore")}>
        ← Back to Semaphore
      </button>
    </>
  );
}
